import Mention from '@tiptap/extension-mention';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { requestVariableFocus } from '../utils/variableFocusRegistry';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import {
  VARIABLE_NODE_NAME,
  VARIABLE_DATA_TYPE,
  variableHtmlAttributes,
  variableDisplayText,
} from '../utils/variableNodes';
import { loadVariableCatalog } from '../utils/variableCatalogRegistry';
import { filterVariableCatalog, flattenFilteredGroups } from '../utils/variableCatalog';
import {
  openVariableSuggestion,
  closeVariableSuggestion,
  moveVariableSuggestionSelection,
  commitVariableSuggestionSelection,
} from '../utils/variableSuggestionRegistry';

/**
 * Variable `@` insérable dans les textes riches du canvas.
 *
 * Nœud atomique inline portant `{ key, name, value }`, sérialisé en un `<span>`
 * autonome (cf. `utils/variableNodes.js`) : le viewer public rend la valeur
 * stockée, sans appel API.
 */

/* La troncature de la liste vit dans le registre du menu : elle doit s'appliquer
   après le champ de recherche, pas avant, sinon celui-ci ne chercherait que dans
   les 60 premières variables. */
export { VARIABLE_SUGGESTION_LIMIT } from '../utils/variableSuggestionRegistry';

/** Classe posée par décoration sur une variable prise dans la sélection.
 *  Purement visuelle et propre à l'édition : jamais persistée. */
export const VARIABLE_SELECTED_CLASS = 'proposal-variable--selected';

const attribute = (dataAttr, { boolean = false } = {}) => ({
  default: boolean ? false : null,
  parseHTML: (element) => (boolean
    ? element.getAttribute(dataAttr) === 'true'
    : element.getAttribute(dataAttr)),
  // Les attributs DOM sont écrits en bloc par `renderHTML` ci-dessous : les
  // laisser s'ajouter ici les dupliquerait.
  renderHTML: () => ({}),
});

export const ProposalVariable = Mention.extend({
  name: VARIABLE_NODE_NAME,

  addAttributes() {
    return {
      key: attribute('data-variable-key'),
      name: attribute('data-variable-name'),
      value: attribute('data-variable-value'),
      orphan: attribute('data-variable-orphan', { boolean: true }),
      // Nombre de lignes d'une adresse et séparateur, propres à l'occurrence.
      lineStyle: attribute('data-variable-line-style'),
      separator: attribute('data-variable-separator'),
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${VARIABLE_DATA_TYPE}"]` }];
  },

  renderHTML({ node }) {
    return ['span', variableHtmlAttributes(node.attrs), variableDisplayText(node.attrs)];
  },

  renderText({ node }) {
    return variableDisplayText(node.attrs);
  },

  /**
   * Surlignage de la variable prise dans une sélection de texte.
   *
   * Un nœud atomique est rendu `contenteditable="false"` par ProseMirror, et
   * les navigateurs ne peignent pas la sélection sur ces îlots : la variable
   * restait blanche au milieu d'un passage surligné, comme si elle n'était pas
   * prise. La décoration rétablit le repère visuel.
   */
  addProseMirrorPlugins() {
    const variableType = this.type;
    return [
      // `Mention` pose ici le plugin `Suggestion` : sans ce parent, le menu `@`
      // ne s'ouvre plus du tout.
      ...(this.parent?.() || []),
      new Plugin({
        key: new PluginKey('proposalVariableSelection'),
        props: {
          handleDoubleClickOn(view, pos, node) {
            if (node.type !== variableType) return false;
            requestVariableFocus(node.attrs.key);
            return true;
          },
          decorations(state) {
            const { from, to, empty } = state.selection;
            if (empty) return null;
            const decorations = [];
            state.doc.nodesBetween(from, to, (node, pos) => {
              if (node.type !== variableType) return;
              // Seulement les variables entièrement prises : une sélection qui
              // s'arrête à leur bord ne les emporte pas.
              if (pos >= from && pos + node.nodeSize <= to) {
                decorations.push(
                  Decoration.node(pos, pos + node.nodeSize, { class: VARIABLE_SELECTED_CLASS }),
                );
              }
            });
            return decorations.length
              ? DecorationSet.create(state.doc, decorations)
              : null;
          },
        },
      }),
    ];
  },
});

/**
 * Items du menu : catalogue chargé paresseusement (premier `@` tapé), filtré
 * sur les mots saisis, mis à plat en gardant l'ordre des groupes.
 */
async function suggestionItems({ query }) {
  const catalog = await loadVariableCatalog();
  return flattenFilteredGroups(filterVariableCatalog(catalog, query));
}

/**
 * Marques du texte que le `@` remplace : police, taille, couleur, gras…
 * Sans elles, la variable insérée retomberait sur le style par défaut du bloc
 * au milieu d'une phrase déjà mise en forme.
 */
function marksAtInsertion(editor, range) {
  const marks = editor.state.storedMarks
    || editor.state.doc.resolve(range.from).marks();
  return marks?.length ? marks.map((mark) => mark.toJSON()) : undefined;
}

/** Insertion : le nœud porte clé, nom et valeur du catalogue au moment du choix. */
function insertVariable({ editor, range, props }) {
  const marks = marksAtInsertion(editor, range);
  editor
    .chain()
    .focus()
    .insertContentAt(range, [
      {
        type: VARIABLE_NODE_NAME,
        attrs: {
          key: props.key,
          name: props.name,
          value: props.value || '',
          orphan: false,
        },
        ...(marks ? { marks } : {}),
      },
      // L'espace qui suit porte les mêmes marques : la frappe reprend dans le
      // style de la phrase, pas dans celui par défaut.
      { type: 'text', text: ' ', ...(marks ? { marks } : {}) },
    ])
    .run();
}

/**
 * Passerelle plugin ProseMirror → registre du menu. Aucun état React ici : le
 * menu s'abonne au registre, seul l'éditeur propriétaire se re-rend.
 */
function suggestionRenderer() {
  let owner = null;

  const publish = (props) => {
    owner = props.editor;
    openVariableSuggestion({
      owner,
      items: props.items,
      clientRect: props.clientRect,
      onSelect: (variable) => props.command(variable),
    });
  };

  return {
    onStart: publish,
    onUpdate: publish,
    onExit: () => {
      closeVariableSuggestion(owner);
      owner = null;
    },
    // `Escape` est traité par le plugin lui-même (il ferme la suggestion).
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowDown') return moveVariableSuggestionSelection(1);
      if (event.key === 'ArrowUp') return moveVariableSuggestionSelection(-1);
      if (event.key === 'Enter' || event.key === 'Tab') {
        return commitVariableSuggestionSelection();
      }
      return false;
    },
  };
}

export const ProposalVariableSuggestion = ProposalVariable.configure({
  suggestion: {
    char: '@',
    // Recherche multi-mots, comme l'éditeur classique (« nom offre »).
    allowSpaces: true,
    /* Le `@` doit suivre un début de bloc ou une espace — mais en
       contenteditable, une espace tapée en fin de ligne devient une **espace
       insécable**. Sans elle dans la liste, taper « variable, espace, @ » ne
       rouvrait pas le menu (le défaut de Tiptap n'accepte que `' '`). */
    allowedPrefixes: [' ', '\u00A0'],
    items: suggestionItems,
    command: insertVariable,
    render: suggestionRenderer,
  },
});
